import {
    MSAC_FINAL_RESULTS_FAILURE,
    MSAC_FINAL_RESULTS_REQUEST,
    MSAC_FINAL_RESULTS_SUCCESS,
    COMPETITION_RESULT_WINNER_SUCCESS,
    COMPETITION_RESULT_PATCH_SUCCESS,
    SCORE_ENTRY_SUCCESS,
    SCORE_ROUND_COMPLETE_SUCCESS,
    SCORE_ROUND_REOPEN_SUCCESS,
    OFFICIAL_RESULTS_CONFIRM_SUCCESS,
    CORRECTION_RESET_RESULTS_SUCCESS,
    CORRECTION_REGENERATE_OFFICIAL_RESULTS_SUCCESS,
    SHOOTING_RESULT_SAVE_SUCCESS,
    SHOOTING_RESULT_REMOVE_SUCCESS,
} from 'actions';

/** The round robin's table was written (RoundRobinPanel). */
const ROUND_ROBIN_SAVED = 'ROUND_ROBIN_SAVED';

/** The competition's rating level changed — the rating column with it (RatingPoints, 15 Oct 2026). */
export const RATING_LEVEL_SAVED = 'RATING_LEVEL_SAVED';

/** The competition's Додаток 10 rank changed — every athlete's points with it (PlacePoints, 17 Oct 2026). */
export const PLACE_POINTS_SAVED = 'PLACE_POINTS_SAVED';

/** The competition an action concerns, as an id — or null. */
const competitionOf = action => {
    const competition = action.competition
        || (action.result && action.result.competition ? action.result.competition : null);

    if ('string' === typeof competition) {
        return competition;
    }

    return competition && competition.id ? competition.id : null;
};

const initialState = {
    // competitionId → MsacFinalResults payload (categories → ranked athletes)
    dataByCompetition: {},
    // competitionId → backend validation message (422) to display verbatim
    errorByCompetition: {},
    isFetched: [],
    isFetching: [],
    // competitionIds whose results changed while a read was in flight — that
    // read answers for the data as it was when SENT (CategoryOverview's
    // reducer does the same).
    invalidatedInFlight: [],
};

/**
 * Drops a competition's answer so its tabs re-read. A read already in flight
 * cannot be trusted either: a second shooting score saved while the first
 * one's read was on its way was never shown (14 Oct 2026) — the read came
 * back with one shooter, was marked fetched, and nothing asked again.
 */
const invalidate = (state, changed) => ({
    ...state,
    isFetched: state.isFetched.filter(id => id !== changed),
    invalidatedInFlight: state.isFetching.includes(changed) && !(state.invalidatedInFlight || []).includes(changed)
        ? [...(state.invalidatedInFlight || []), changed]
        : (state.invalidatedInFlight || []),
});

/** A read finished: fetched — unless the data changed meanwhile, then not, so it is read again. */
const settled = (state, competitionId) => {
    const outdated = (state.invalidatedInFlight || []).includes(competitionId);
    return {
        isFetched: outdated
            ? state.isFetched.filter(id => id !== competitionId)
            : [...state.isFetched.filter(id => id !== competitionId), competitionId],
        isFetching: state.isFetching.filter(id => id !== competitionId),
        invalidatedInFlight: (state.invalidatedInFlight || []).filter(id => id !== competitionId),
    };
};

export const msacFinalResultListReducer = (state = initialState, action) => {
    const { type, data, competitionId, message } = action;
    switch (type) {
        case MSAC_FINAL_RESULTS_SUCCESS: {
            const errors = { ...state.errorByCompetition };
            delete errors[competitionId];
            return {
                ...state,
                ...settled(state, competitionId),
                dataByCompetition: {
                    ...state.dataByCompetition,
                    [competitionId]: data,
                },
                errorByCompetition: errors,
            };
        }

        case MSAC_FINAL_RESULTS_REQUEST:
            return {
                ...state,
                isFetching: [...state.isFetching, competitionId],
            };

        case MSAC_FINAL_RESULTS_FAILURE: {
            const cleared = { ...state.dataByCompetition };
            delete cleared[competitionId];
            return {
                ...state,
                // fetched (we have a definitive answer) but with an error
                ...settled(state, competitionId),
                dataByCompetition: cleared,
                errorByCompetition: {
                    ...state.errorByCompetition,
                    [competitionId]: message || null,
                },
            };
        }

        // A RESULT CHANGED — a winner picked, a score, a round robin's points
        // or judges' order, a confirmation or a correction. The final result
        // tab reads places from these, so its cached answer is dropped and the
        // tab re-reads (4 Oct 2026: it went on showing the old order).
        case COMPETITION_RESULT_WINNER_SUCCESS:
        case COMPETITION_RESULT_PATCH_SUCCESS:
        case SCORE_ENTRY_SUCCESS:
        case SCORE_ROUND_COMPLETE_SUCCESS:
        case SCORE_ROUND_REOPEN_SUCCESS:
        case OFFICIAL_RESULTS_CONFIRM_SUCCESS:
        case CORRECTION_RESET_RESULTS_SUCCESS:
        case CORRECTION_REGENERATE_OFFICIAL_RESULTS_SUCCESS:
        case ROUND_ROBIN_SAVED:
        case RATING_LEVEL_SAVED:
        case PLACE_POINTS_SAVED: {
            const changed = competitionOf(action);
            if (null === changed) {
                return state;
            }

            return invalidate(state, changed);
        }

        // A SHOOTING SCORE SAVED — the shooting places and ties of every
        // category the athlete is in may have moved (14 Oct 2026: the
        // shooting tab shows them beside the scores).
        case SHOOTING_RESULT_SAVE_SUCCESS:
        case SHOOTING_RESULT_REMOVE_SUCCESS:
            return invalidate(state, action.competitionId);

        default:
            return state;
    }
};
